import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserPeople, createPerson, updatePerson, mergePeople, deletePerson } from '../../services/dashboardService';
import { 
  Users, 
  ArrowLeft, 
  User, 
  UserCheck, 
  UserCog,
  Building,
  ExternalLink,
  MessageSquare,
  ChevronRight,
  Loader2,
  Search,
  Plus,
  Pencil,
  Merge,
  Trash2,
  MoreVertical,
  AlertTriangle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

const RELATIONSHIP_ICONS = {
  direct_report: UserCheck,
  manager: UserCog,
  peer: User,
  skip_level: Building,
  stakeholder: Building,
  external: ExternalLink
};

const RELATIONSHIP_LABELS = {
  direct_report: 'Direct Report',
  manager: 'Manager',
  peer: 'Peer',
  skip_level: 'Skip Level',
  stakeholder: 'Stakeholder',
  external: 'External'
};

const RELATIONSHIP_COLORS = {
  direct_report: 'bg-green-500/10 text-green-500',
  manager: 'bg-purple-500/10 text-purple-500',
  peer: 'bg-blue-500/10 text-blue-500',
  skip_level: 'bg-orange-500/10 text-orange-500',
  stakeholder: 'bg-yellow-500/10 text-yellow-500',
  external: 'bg-zinc-500/10 text-zinc-400'
};

const PeoplePage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRelationship, setFilterRelationship] = useState('all');
  
  // Add person modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPerson, setNewPerson] = useState({ name: '', relationship: 'peer', context_notes: '' });
  const [addError, setAddError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  
  // Edit person modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPerson, setEditPerson] = useState(null);
  const [editForm, setEditForm] = useState({ alias: '', relationship: '', context_notes: '' });
  const [editError, setEditError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Merge modal state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeError, setMergeError] = useState(null);
  const [isMerging, setIsMerging] = useState(false);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const loadPeople = async () => {
    try {
      const data = await fetchUserPeople();
      setPeople(data.people || []);
    } catch (err) {
      if (err.message === 'Unauthorized') {
        navigate('/login', { replace: true });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadPeople();
    }
  }, [isAuthenticated, navigate]);

  // Generate alias from name (Name.X format)
  const generateAlias = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    const firstName = trimmed.split(' ')[0];
    const lastName = trimmed.split(' ')[1];
    const suffix = lastName ? lastName.charAt(0).toUpperCase() : 'X';
    return `${firstName.charAt(0).toUpperCase()}${firstName.slice(1).toLowerCase()}.${suffix}`;
  };

  // Handle add person submission
  const handleAddPerson = async () => {
    setAddError(null);
    
    if (!newPerson.name.trim()) {
      setAddError('Please enter a name');
      return;
    }
    
    const alias = generateAlias(newPerson.name);
    
    // Check if alias already exists
    if (people.some(p => p.alias.toLowerCase() === alias.toLowerCase())) {
      setAddError(`A person with alias "${alias}" already exists. Try using their full name or a different identifier.`);
      return;
    }
    
    setIsAdding(true);
    
    try {
      await createPerson({
        alias: alias,
        relationship: newPerson.relationship,
        context_notes: newPerson.context_notes.trim() || null
      });
      
      // Refresh the people list
      await loadPeople();
      
      // Reset form and close modal
      setNewPerson({ name: '', relationship: 'peer', context_notes: '' });
      setShowAddModal(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setIsAdding(false);
    }
  };

  // Open edit modal for a person
  const openEditModal = (person) => {
    setEditPerson(person);
    setEditForm({
      alias: person.alias,
      relationship: person.relationship,
      context_notes: person.context_notes || ''
    });
    setEditError(null);
    setShowEditModal(true);
  };

  // Handle edit person submission
  const handleEditPerson = async () => {
    setEditError(null);
    
    if (!editForm.alias.trim()) {
      setEditError('Alias cannot be empty');
      return;
    }
    
    setIsEditing(true);
    
    try {
      await updatePerson(editPerson.alias, {
        alias: editForm.alias !== editPerson.alias ? editForm.alias : undefined,
        relationship: editForm.relationship,
        context_notes: editForm.context_notes.trim() || null
      });
      
      await loadPeople();
      setShowEditModal(false);
      setEditPerson(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setIsEditing(false);
    }
  };

  // Open merge modal for a person
  const openMergeModal = (person) => {
    setMergeSource(person);
    setMergeTarget('');
    setMergeError(null);
    setShowMergeModal(true);
  };

  // Handle merge submission
  const handleMergePeople = async () => {
    setMergeError(null);
    
    if (!mergeTarget) {
      setMergeError('Please select a person to merge into');
      return;
    }
    
    setIsMerging(true);
    
    try {
      await mergePeople(mergeSource.alias, mergeTarget);
      await loadPeople();
      setShowMergeModal(false);
      setMergeSource(null);
    } catch (err) {
      setMergeError(err.message);
    } finally {
      setIsMerging(false);
    }
  };

  // Handle delete person
  const handleDeletePerson = async () => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    
    try {
      await deletePerson(deleteTarget.alias);
      await loadPeople();
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter people based on search and relationship
  const filteredPeople = people.filter(person => {
    const matchesSearch = person.alias.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (person.context_notes || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterRelationship === 'all' || person.relationship === filterRelationship;
    return matchesSearch && matchesFilter;
  });

  // Group by relationship
  const groupedPeople = filteredPeople.reduce((acc, person) => {
    const rel = person.relationship || 'peer';
    if (!acc[rel]) acc[rel] = [];
    acc[rel].push(person);
    return acc;
  }, {});

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">Your People</h1>
                <p className="text-zinc-500 text-sm">People in your network</p>
              </div>
            </div>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="add-person-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Person
            </Button>
          </div>
        </div>
      </header>

      {/* Add Person Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a Person</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add someone to your network to track interactions and generate prep notes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {addError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {addError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="person-name" className="text-zinc-300">Name</Label>
              <Input
                id="person-name"
                placeholder="e.g., Sarah Mitchell"
                value={newPerson.name}
                onChange={(e) => setNewPerson(prev => ({ ...prev, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                data-testid="add-person-name-input"
              />
              {newPerson.name.trim() && (
                <p className="text-xs text-zinc-500">
                  Will be saved as: <span className="text-zinc-300">{generateAlias(newPerson.name)}</span>
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="person-relationship" className="text-zinc-300">Relationship</Label>
              <Select
                value={newPerson.relationship}
                onValueChange={(value) => setNewPerson(prev => ({ ...prev, relationship: value }))}
              >
                <SelectTrigger 
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="add-person-relationship-select"
                >
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="direct_report" className="text-white hover:bg-zinc-700">Direct Report</SelectItem>
                  <SelectItem value="manager" className="text-white hover:bg-zinc-700">Manager</SelectItem>
                  <SelectItem value="peer" className="text-white hover:bg-zinc-700">Peer</SelectItem>
                  <SelectItem value="skip_level" className="text-white hover:bg-zinc-700">Skip Level</SelectItem>
                  <SelectItem value="stakeholder" className="text-white hover:bg-zinc-700">Stakeholder</SelectItem>
                  <SelectItem value="external" className="text-white hover:bg-zinc-700">External</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="person-notes" className="text-zinc-300">Notes (optional)</Label>
              <Input
                id="person-notes"
                placeholder="e.g., Product Manager on Project Alpha"
                value={newPerson.context_notes}
                onChange={(e) => setNewPerson(prev => ({ ...prev, context_notes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                data-testid="add-person-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowAddModal(false);
                setNewPerson({ name: '', relationship: 'peer', context_notes: '' });
                setAddError(null);
              }}
              className="text-zinc-400 hover:text-white"
              data-testid="add-person-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddPerson}
              disabled={isAdding || !newPerson.name.trim()}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="add-person-submit-button"
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Person'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Person Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Person</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update details for {editPerson?.alias}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {editError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="edit-alias" className="text-zinc-300">Alias</Label>
              <Input
                id="edit-alias"
                value={editForm.alias}
                onChange={(e) => setEditForm(prev => ({ ...prev, alias: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="edit-person-alias-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-relationship" className="text-zinc-300">Relationship</Label>
              <Select
                value={editForm.relationship}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, relationship: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="edit-person-relationship-select">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="direct_report" className="text-white hover:bg-zinc-700">Direct Report</SelectItem>
                  <SelectItem value="manager" className="text-white hover:bg-zinc-700">Manager</SelectItem>
                  <SelectItem value="peer" className="text-white hover:bg-zinc-700">Peer</SelectItem>
                  <SelectItem value="skip_level" className="text-white hover:bg-zinc-700">Skip Level</SelectItem>
                  <SelectItem value="stakeholder" className="text-white hover:bg-zinc-700">Stakeholder</SelectItem>
                  <SelectItem value="external" className="text-white hover:bg-zinc-700">External</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-notes" className="text-zinc-300">Notes</Label>
              <Input
                id="edit-notes"
                value={editForm.context_notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, context_notes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500"
                placeholder="e.g., Product Manager on Project Alpha"
                data-testid="edit-person-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setShowEditModal(false); setEditPerson(null); }}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditPerson}
              disabled={isEditing || !editForm.alias.trim()}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="edit-person-submit-button"
            >
              {isEditing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge People Modal */}
      <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5" />
              Merge People
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Merge <span className="text-white font-medium">{mergeSource?.alias}</span> into another person. 
              All reflections and data will be transferred.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {mergeError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {mergeError}
              </div>
            )}
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-sm flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">This action cannot be undone</p>
                <p className="text-amber-400/80 mt-1">
                  {mergeSource?.alias} will be deleted and all their data merged into the selected person.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Merge into</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="merge-target-select">
                  <SelectValue placeholder="Select person to merge into" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {people
                    .filter(p => p.alias !== mergeSource?.alias)
                    .map(p => (
                      <SelectItem key={p.alias} value={p.alias} className="text-white hover:bg-zinc-700">
                        {p.alias} ({RELATIONSHIP_LABELS[p.relationship] || p.relationship})
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setShowMergeModal(false); setMergeSource(null); }}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMergePeople}
              disabled={isMerging || !mergeTarget}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              data-testid="merge-submit-button"
            >
              {isMerging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                'Merge People'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Person?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete <span className="text-white font-medium">{deleteTarget?.alias}</span>? 
              This will not delete any reflections, but the person will no longer appear in your network.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDeletePerson}
              disabled={isDeleting}
              data-testid="delete-confirm-button"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
              data-testid="people-search-input"
            />
          </div>
          <select
            value={filterRelationship}
            onChange={(e) => setFilterRelationship(e.target.value)}
            className="px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-zinc-700"
            data-testid="people-filter-select"
          >
            <option value="all">All Relationships</option>
            <option value="direct_report">Direct Reports</option>
            <option value="manager">Managers</option>
            <option value="peer">Peers</option>
            <option value="skip_level">Skip Level</option>
            <option value="stakeholder">Stakeholders</option>
            <option value="external">External</option>
          </select>
        </div>

        {error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
            {error}
          </div>
        ) : filteredPeople.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {people.length === 0 ? 'No people saved yet' : 'No matches found'}
            </h2>
            <p className="text-zinc-400 max-w-md mx-auto">
              {people.length === 0 
                ? 'When you mention colleagues in your reflections, the AI companion will suggest saving them for context.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedPeople).map(([relationship, groupPeople]) => (
              <div key={relationship}>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  {React.createElement(RELATIONSHIP_ICONS[relationship] || User, { className: 'w-5 h-5 text-zinc-400' })}
                  {RELATIONSHIP_LABELS[relationship] || relationship}
                  <span className="text-zinc-500 font-normal">({groupPeople.length})</span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupPeople.map((person) => (
                    <PersonCard 
                      key={person.alias} 
                      person={person}
                      onEdit={() => openEditModal(person)}
                      onMerge={() => openMergeModal(person)}
                      onDelete={() => { setDeleteTarget(person); setShowDeleteConfirm(true); }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const PersonCard = ({ person, onEdit, onMerge, onDelete }) => {
  const navigate = useNavigate();
  const IconComponent = RELATIONSHIP_ICONS[person.relationship] || User;
  const colorClass = RELATIONSHIP_COLORS[person.relationship] || RELATIONSHIP_COLORS.peer;

  const handleClick = () => {
    navigate(`/dashboard/people/${encodeURIComponent(person.alias)}`);
  };

  const handleChatClick = (e) => {
    e.stopPropagation();
    navigate(`/dashboard/chat?person=${encodeURIComponent(person.alias)}`);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors cursor-pointer group"
      data-testid={`person-card-${person.alias}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium">{person.alias}</h3>
          <p className="text-zinc-500 text-sm">
            {RELATIONSHIP_LABELS[person.relationship] || person.relationship}
          </p>
        </div>
        
        {/* Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-zinc-500 hover:text-white hover:bg-zinc-800"
              data-testid={`person-actions-${person.alias}`}
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
              data-testid={`person-edit-${person.alias}`}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); onMerge(); }}
              className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
              data-testid={`person-merge-${person.alias}`}
            >
              <Merge className="w-4 h-4 mr-2" />
              Merge with...
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-red-400 hover:text-red-300 hover:bg-zinc-800 cursor-pointer"
              data-testid={`person-delete-${person.alias}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {person.context_notes && (
        <p className="mt-3 text-zinc-400 text-sm line-clamp-2">{person.context_notes}</p>
      )}
      
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-zinc-500">
          {person.mention_count} mention{person.mention_count !== 1 ? 's' : ''}
        </span>
        <button
          onClick={handleChatClick}
          className="flex items-center gap-1 text-purple-400 hover:text-purple-300 transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Chat
        </button>
      </div>
    </div>
  );
};

export default PeoplePage;
